/**
 * SEO landing for the query cluster "személyi edző Miskolc".
 *
 * Standalone route at /szemelyi-edzo-miskolc with:
 *  - a single H1 containing the target keyword
 *  - unique long-form Hungarian copy (not duplicated from the homepage)
 *  - HealthClub JSON-LD (Schema.org) so the Google knowledge panel picks
 *    up name/address/phone/opening-hours
 *  - a FAQ block that is ALSO emitted as FAQPage JSON-LD so Google can
 *    render the accordion right in the SERP
 *  - CTA that routes to /#contact where the free 20-minute booking calendar
 *    is already wired up (same flow the other landing pages use).
 *
 * Included in sitemap.xml (backend `routes/seo.py`) and linked from Header
 * + Footer for internal PageRank flow.
 */
import React, { useState } from 'react';
import { usePaidAssessment } from '../utils/usePublicConfig';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ChevronRight, CheckCircle2, ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { Button } from '../components/ui/button';
import { GYM_VENUE } from '../config/venue';
import QualificationDialog from '../components/QualificationDialog';
import BookingModal from '../components/BookingModal';
import ReviewsSection from '../components/ReviewsSection';
import PromoPopupModal from '../components/PromoPopupModal';
import usePromoPopup from '../hooks/usePromoPopup';
import PhotoSlider from '../components/PhotoSlider';
import { ABOUT_PHOTOS_SMALL } from '../data/aboutPhotos';
import PromoHeroCard from '../components/PromoHeroCard';
import { trackLead } from '../utils/metaPixel';

const SITE_ORIGIN = 'https://oroszcoaching.hu';
const CANONICAL = `${SITE_ORIGIN}/szemelyi-edzo-miskolc`;

const FAQ_ITEMS = [
  {
    q: 'Mennyibe kerül a személyi edzés Miskolcon?',
    a: ('Az 1:1 edzői mentorálás díja programtól függően havi 25.000 Ft-tól indul (online), a személyes programok 60.000 Ft/hó-tól. '
        + 'A pontos árakat és a benne foglaltakat a Programok oldalon találod. A BB Gym terembérlet nem része az árnak, '
        + 'azt a helyszínen külön kell megváltani.'),
  },
  {
    q: 'Kezdőként is jelentkezhetek?',
    a: ('Igen — a programjaim jelentős része kifejezetten kezdőknek szól. Ítélkezésmentes közeget adok, ahol a saját tempódban '
        + 'haladhatsz, akár kiscsoportban, akár egyedül.'),
  },
  {
    q: 'Kell már edzéstapasztalat?',
    a: ('Nem. Ha még soha nem edzettél, a részletes felméréssel és a személyre szabott tervvel a nulláról is biztonságosan '
        + 'elkezdheted.'),
  },
  {
    q: 'Van-e ingyenes alkalom?',
    a: 'Az első, 20 perces ismerkedő konzultáció ingyenes és kötelezettségmentes. Személyesen a BB Gymben vagy online – ahogy neked kényelmes.',
  },
  {
    q: 'Edzhetek a barátommal vagy a párommal?',
    a: ('Igen, a kiscsoportos (Duó & Trió) program pontosan erre való: max. 3 fő, közös időpont, mindenki a saját tervével.'),
  },
  {
    q: 'Vállalsz online edzést is?',
    a: ('Igen, a teljesen online, havi díjas coaching ország bármely pontjáról elérhető, heti videós forma- '
        + 'és technikaelemzéssel.'),
  },
];

// HealthClub + FAQPage JSON-LD merged into a single @graph so both signals
// reach Google in one shot. Coordinates are BB Gym Miskolc (Szeles u. 71.).
const buildJsonLd = () => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type':       'HealthClub',
      '@id':         `${CANONICAL}#business`,
      name:          'Orosz Coaching',
      alternateName: 'Orosz Coaching – Személyi edző Miskolc',
      sameAs: ['https://www.google.com/maps/place/Orosz+Coaching/@48.1081874,20.7947038,17z/data=!4m17!1m8!3m7!1s0x47409fa8a70d1285:0xc33e2cfae977d768!2sOrosz+Coaching!8m2!3d48.1081838!4d20.7972787!10e1!16s%2Fg%2F11zglvmh37'],
      description:   ('Tudományosan megalapozott 1:1 és kiscsoportos személyi edzés Miskolcon, a BB Gymben. '
                     + 'Kezdőknek és haladóknak.'),
      url:           CANONICAL,
      image:         `${SITE_ORIGIN}/og-image.png`,
      telephone:     '+36 70 237 8299',
      email:         'info@oroszcoaching.hu',
      priceRange:    '45.000–75.000 Ft',
      address: {
        '@type':          'PostalAddress',
        streetAddress:    'Szeles u. 71.',
        addressLocality:  'Miskolc',
        postalCode:       '3526',
        addressCountry:   'HU',
      },
      geo: {
        '@type':   'GeoCoordinates',
        latitude:  '48.1000',
        longitude: '20.7833',
      },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'],
          opens: '05:00', closes: '21:00' },
        { '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday','Sunday'],
          opens: '07:00', closes: '20:00' },
      ],
      founder:    { '@type': 'Person', name: 'Orosz Gábor' },
      areaServed: 'Miskolc',
    },
    {
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type':          'Question',
        name:             item.q,
        acceptedAnswer:   { '@type': 'Answer', text: item.a },
      })),
    },
  ],
});

const AUDIENCE_ROWS = [
  {
    label: 'Kezdőknek',
    body:  'akik félnek egyedül lemenni a terembe, és biztonságos, ítélkezésmentes közeget keresnek.',
  },
  {
    label: 'Visszatérőknek',
    body:  'akik hosszabb kihagyás, sérülés vagy szülés után térnének vissza a mozgáshoz.',
  },
  {
    label: 'Ülőmunkát végzőknek és műszakos dolgozóknak',
    body:  'akiknek a jobb tartás, a kevésbé merev, fáradt hát és a fenntartható fogyás a fő cél.',
  },
  {
    label: 'Haladóknak',
    body:  'akik mérhető izomtömeg- és erőnövekedést szeretnének tudományos alapú tervezéssel.',
  },
];

const PROGRAM_ROWS = [
  {
    title: 'Életmódváltó Program',
    body:  'kezdőknek, ülőmunkát végzőknek, műszakosoknak. Jobb tartás, kevésbé merev és fáradt hát, fenntartható fogyás koplalás nélkül.',
    route: '/eletmodvalto',
  },
  {
    title: 'Izomépítés és formázás',
    body:  'közép- és haladó szintre, tudományos alapú izomtömeg- és erőnövelés.',
    route: '/hipertrofia',
  },
  {
    title: 'Kiscsoportos edzés (Duó & Trió)',
    body:  'a személyi edzés minősége max. 3 fős, zárt csoportban, akár barátoddal vagy pároddal.',
    route: '/kiscsoportos-edzes',
  },
  {
    title: '1:1 Online Mentorálás',
    body:  'teljesen rugalmas, havi díjas online kísérés heti videós technikaelemzéssel.',
    route: '/premium-coaching',
  },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-gray-200 last:border-b-0" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-5 flex items-center justify-between gap-4 group"
        aria-expanded={open}
      >
        <span className="text-base md:text-lg font-semibold text-gray-900 group-hover:text-yellow-700 transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180 text-yellow-600' : ''}`}
        />
      </button>
      {/* A válasz mindig a DOM-ban van (SEO), csak vizuálisan csukott. */}
      <div
        className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        aria-hidden={!open}
      >
        <p className="overflow-hidden text-gray-700 leading-relaxed pr-8" data-testid={`faq-answer-${idx}`}>
          <span className="block pb-5">{a}</span>
        </p>
      </div>
    </div>
  );
};

export default function SzemelyiEdzoMiskolcPage() {
  const paidAssessment = usePaidAssessment();

  useDocumentMeta({
    title:       'Személyi edző Miskolcon – 1:1 és kiscsoportos edzés | Orosz Coaching',
    description: ('Személyi edző Miskolcon, a BB Gymben. Tudományosan megalapozott 1:1 és max. 3 fős kiscsoportos '
                 + 'edzés kezdőknek és haladóknak. Foglalj ingyenes 20 perces konzultációt.'),
    canonical:   CANONICAL,
    image:       `${SITE_ORIGIN}/og-image.png`,
    type:        'website',
    jsonLd:      buildJsonLd(),
    keywords:    ('személyi edző Miskolc, személyi edzés Miskolc, edző Miskolc, kiscsoportos edzés Miskolc, '
                 + 'alakformálás Miskolc, fogyás Miskolc, izomépítés Miskolc, BB Gym, Orosz Gábor'),
  });

  const [bookingOpen, setBookingOpen] = useState(false);
  const goToBooking = () => setBookingOpen(true);

  // Promo popup — same schedule / cooldown / version rules as the homepage,
  // so a returning visitor who dismissed it on / won't see it again here.
  const promoPopup = usePromoPopup({ enabled: true });
  const handlePromoCta = () => {
    trackLead({ content_name: `promo_popup:${promoPopup.promo?.version || 'default'}:miskolc` });
    promoPopup.reportClick();
    promoPopup.close();
    if (promoPopup.promo?.cta_action === 'link' && promoPopup.promo?.cta_href) {
      window.location.assign(promoPopup.promo.cta_href);
      return;
    }
    try { sessionStorage.setItem('oc_from_promo_cta', promoPopup.promo?.version || '1'); } catch { /* ignore */ }
    setBookingOpen(true);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Header onBookClick={goToBooking} />

      {/* ============================================================ */}
      {/*  HERO — single H1 with the target keyword, dominant CTA row  */}
      {/* ============================================================ */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 text-white overflow-hidden pt-28 md:pt-32 pb-16 md:pb-20">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(202,138,4,0.35), transparent 45%), radial-gradient(circle at 80% 60%, rgba(202,138,4,0.25), transparent 45%)" }} />
        <div className="relative container mx-auto px-6 max-w-6xl grid lg:grid-cols-[minmax(0,1fr)_370px] gap-10 lg:gap-14 items-center">
          <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 mb-5">
            <MapPin size={12} /> Miskolc · BB Gym
          </div>
          <h1
            data-testid="seo-page-h1"
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-5"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Személyi edző Miskolcon —<br className="hidden md:block" /> 1:1 és kiscsoportos edzés
          </h1>
          <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mb-8">
            Tudományosan megalapozott személyi edzés Miskolcon, a BB Gymben — kezdőtől a haladóig, mindig a te céljaidra szabva.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="seo-hero-cta-book"
              onClick={goToBooking}
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-base h-12 px-6"
            >
              Foglalj ingyenes konzultációt <ChevronRight size={18} className="ml-1" />
            </Button>
            <a
              href="#programok"
              data-testid="seo-hero-cta-programs"
              className="inline-flex items-center rounded-md border border-white/25 hover:border-yellow-500/60 hover:text-yellow-300 text-white font-semibold h-12 px-6 transition-colors"
            >
              Programok megtekintése
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> Ingyenes első konzultáció</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> Rád szabott terv</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> Heti technikai visszajelzés</span>
          </div>
          </div>
          <div className="lg:justify-self-end w-full flex lg:block justify-center">
            <PromoHeroCard onBooking={goToBooking} testId="promo-hero-card-miskolc" />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  BODY — unique long-form Hungarian copy, no duplication of   */}
      {/*  the homepage text (Google penalises verbatim duplicates).   */}
      {/* ============================================================ */}
      <section className="container mx-auto px-6 py-16 md:py-20 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,260px)_1fr] gap-6 md:gap-8 items-start mb-10">
          <div className="rounded-lg overflow-hidden shadow-xl aspect-[4/5] md:aspect-auto md:h-[320px] w-full max-w-sm mx-auto md:max-w-none md:mx-0">
            <PhotoSlider
              photos={ABOUT_PHOTOS_SMALL}
              testId="miskolc-about-photo"
              className="w-full h-full"
            />
          </div>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
            <strong>Orosz Gábor</strong> vagyok, okleveles{' '}
            <QualificationDialog
              label="testépítés-fitnesz sportedző"
              testId="miskolc-qualification-trigger"
            />
            . Ha személyi edzőt keresel Miskolcon, aki nem sablonprogramot ad, hanem a te testedhez, mozgásodhoz és céljaidhoz igazított tervet, akkor jó helyen jársz. Nálam nincs kísérletezgetés és nincsenek divatdiéták — minden döntés mögött a sporttudomány jelenlegi állása áll.
          </p>
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Miért személyi edzés?
        </h2>
        <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10">
          Egyedül, jó terv nélkül a legtöbben ott akadnak el, hogy nem tudják, mit csináljanak, vagy rossz technikával, kapkodva edzenek — és pár hét után feladják. A személyi edzés ezt oldja meg: végig melletted áll valaki, aki figyeli a technikádat, méri a haladásodat, és a tervet folyamatosan a te életviteledhez igazítja. Nem engedem el a kezed az első alkalomtól kezdve.
        </p>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Kinek szól?
        </h2>
        <ul className="space-y-3 mb-10">
          {AUDIENCE_ROWS.map((row) => (
            <li key={row.label} className="flex gap-3 items-start">
              <CheckCircle2 size={20} className="text-yellow-600 shrink-0 mt-0.5" />
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                <strong className="text-gray-900">{row.label}</strong>{row.label.endsWith(',') ? ' ' : ', '}
                {row.body}
              </p>
            </li>
          ))}
        </ul>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Hogyan dolgozom?
        </h2>
        <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10">
          A közös munka mindig egy {paidAssessment ? '' : <strong>ingyenes</strong>}{paidAssessment ? '' : ', '}részletes állapotfelméréssel és kérdőíves anamnézissel kezdődik — enélkül nincs személyre szabott terv. Ezután kapsz egy edzéstervet, amely a testalkatodra, az időbeosztásodra és a céljaidra épül, beépített haladáskövetéssel és heti visszajelzéssel. A hangsúly a fenntartható fejlődésen van: nem a leggyorsabb, hanem a leghosszabb távon is tartható eredményen.
        </p>

        {/* Ügyfélvélemények — ugyanaz a két Google-értékelés, mint a főoldalon */}
      </section>

      <ReviewsSection />

      <section className="container mx-auto px-6 pb-16 md:pb-20 max-w-3xl">
        {/* Hol találsz meg? — with a subtle venue callout */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Hol találsz meg?
        </h2>
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50/60 p-5 md:p-6 mb-12">
          <p className="text-base md:text-lg text-gray-800 leading-relaxed">
            A személyes edzések helyszíne a <strong>{GYM_VENUE.name}</strong> ({GYM_VENUE.fullAddress}). A jól felszerelt terem ideális környezet az 1:1 és a kiscsoportos munkához egyaránt. Ha inkább online kísérést keresel, azt is vállalom — ország bármely pontjáról.
          </p>
          <div className="flex flex-wrap gap-3 items-center mt-4 text-sm">
            <a
              href={GYM_VENUE.mapDeepLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="seo-venue-directions"
              className="inline-flex items-center gap-1.5 text-yellow-800 hover:text-yellow-900 font-semibold underline underline-offset-2"
            >
              <MapPin size={14} /> Útvonaltervezés Google Térképen
            </a>
            <span className="text-gray-500 inline-flex items-center gap-1.5">
              <Clock size={13} /> Hétköznap 05:00–21:00 · Hétvégén 07:00–20:00
            </span>
          </div>

          {/* Interactive Google Map embed — reinforces the address, gives
              visitors a one-tap route to BB Gym, and adds a strong local-SEO
              signal (Google reads embedded Maps as a strong on-page location
              indicator). Uses the free non-API iframe with `output=embed`. */}
          <div
            data-testid="seo-venue-map"
            className="mt-5 rounded-xl overflow-hidden border border-yellow-200/60 aspect-[16/9] bg-yellow-100/40"
          >
            <iframe
              title={`${GYM_VENUE.name} — Térkép`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(GYM_VENUE.fullAddress)}&z=16&hl=hu&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0, display: 'block' }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Programok — 4-panel grid */}
        <h2
          id="programok"
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 scroll-mt-24"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Programok
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
          {PROGRAM_ROWS.map((p, i) => (
            <Link
              key={p.title}
              to={p.route}
              data-testid={`seo-program-link-${i}`}
              className="group rounded-xl border border-gray-200 hover:border-yellow-500 bg-white p-5 transition-all hover:shadow-md"
            >
              <div className="font-bold text-gray-900 mb-1 flex items-center justify-between gap-2">
                {p.title}
                <ChevronRight size={16} className="text-gray-400 group-hover:text-yellow-600 transition-colors" />
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{p.body}</p>
            </Link>
          ))}
        </div>

        {/* Cross-link to the online landing — helps visitors who aren't in
            Miskolc and reinforces Google's topical graph between the two pages. */}
        <div
          data-testid="miskolc-to-online-cta"
          className="rounded-2xl border border-sky-200 bg-sky-50/60 p-5 md:p-6 mb-12 flex flex-col md:flex-row items-start md:items-center gap-4"
        >
          <MapPin size={20} className="text-sky-600 shrink-0 mt-1 md:mt-0" />
          <div className="flex-1">
            <div className="font-bold text-gray-900">Nem Miskolcon élsz?</div>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-0.5">
              Online is dolgozom az ország bármely pontjáról — heti videós technikaelemzéssel.
            </p>
          </div>
          <Link
            to="/online-szemelyi-edzo"
            data-testid="link-to-online"
            className="inline-flex items-center rounded-md bg-sky-600 hover:bg-sky-500 text-white font-bold h-11 px-5 whitespace-nowrap transition-colors"
          >
            Online coaching <ChevronRight size={16} className="ml-1" />
          </Link>
        </div>

        {/* CTA card */}
        <div className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8 md:p-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Az első lépés ingyenes
          </h2>
          <p className="text-white/85 leading-relaxed mb-6 max-w-2xl">
            Nem kell azonnal döntened. Foglalj egy kötetlen, 20 perces ingyenes konzultációt, ahol átbeszéljük, honnan indulsz, mit szeretnél elérni, és melyik program illik hozzád a leginkább. Semmi elköteleződés.
          </p>
          <Button
            data-testid="seo-bottom-cta-book"
            onClick={goToBooking}
            className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-base h-12 px-6"
          >
            Foglalj ingyenes konzultációt <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FAQ — HTML accordion + FAQPage JSON-LD emitted via jsonLd   */}
      {/* ============================================================ */}
      <section className="bg-gray-50 border-t border-gray-200 py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Gyakori kérdések
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Ha bármit nem találsz itt, írj bátran a <a href="mailto:info@oroszcoaching.hu" className="text-yellow-700 hover:text-yellow-800 font-semibold underline underline-offset-2">info@oroszcoaching.hu</a> címre.
          </p>
          <div className="rounded-2xl border border-gray-200 bg-white px-6" data-testid="seo-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem key={item.q} q={item.q} a={item.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
      <PromoPopupModal
        open={promoPopup.isOpen}
        promo={promoPopup.promo}
        onCtaClick={handlePromoCta}
        onClose={promoPopup.close}
      />
    </div>
  );
}
